import React, { useState, useEffect } from 'react';
import './People.css';
import SendMessageModal from '../components/SendMessageModal';

function People() {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  useEffect(() => {
    // Track page view with GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_title: 'People & Payroll Services - rkube',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }, []);

  const handleOpenModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType('');
  };

  return (
    <div className="service-page-container">
      <div className="services-page-hero enhanced">
        <div className="container">
          <h1>Smarter payroll and team operations</h1>
          <p className="services-page-description">
            Onboard talent, manage payroll, and run recruiting ops with ease. 
            AI simplifies withholdings, compliance, and reporting while keeping 
            your HR stack founder-friendly and scalable.
          </p>
          
          <div className="services-intro">
            <p>From payroll processing to global recruitment, we provide comprehensive HR solutions that grow with your business, whether you're a startup or an enterprise.</p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="service-page-content">
          <section className="services-features-grid">
            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon green">
                  <span className="icon">💸</span>
                </div>
                <h3>Payroll Management</h3>
              </div>
              <p>Automated payroll processing with precise tax calculations, direct deposit options, and comprehensive compliance management. We handle everything from W-2s to quarterly filings.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon blue">
                  <span className="icon">👥</span>
                </div>
                <h3>Global Recruitment</h3>
              </div>
              <p>Streamlined candidate sourcing and hiring across US and India markets. From executive roles to technical positions, we help you build and scale your team efficiently.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon purple">
                  <span className="icon">🏥</span>
                </div>
                <h3>Benefits Administration</h3>
              </div>
              <p>Simplified management of health insurance, retirement plans, and other benefits. We help you design and implement competitive benefits packages that attract top talent.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon gold">
                  <span className="icon">📋</span>
                </div>
                <h3>HR Compliance</h3>
              </div>
              <p>Stay compliant with employment laws and regulations across multiple jurisdictions. Our experts ensure your HR practices meet all legal requirements while minimizing risk.</p>
            </div>
          </section>

          <section className="service-benefits">
            <h2>Benefits</h2>
            <ul className="benefits-list">
              <li>Save time on payroll processing and tax filings</li>
              <li>Attract and retain top talent with streamlined HR processes</li>
              <li>Reduce compliance risks related to employment laws</li>
              <li>Scale your team without scaling administrative overhead</li>
              <li>Provide a better employee experience from onboarding to offboarding</li>
            </ul>
          </section>

          <section className="service-pricing">
            <h2>Payroll Service Pricing</h2>
            <div className="pricing-container">
              <div className="pricing-card starter">
                <h3>Starter</h3>
                <div className="price">Free<span>/month</span></div>
                <p className="pricing-description">For up to 5 employees</p>
                <ul className="pricing-features">
                  <li>Complete payroll management</li>
                  <li>Setup and configuration</li>
                  <li>Ongoing monitoring</li>
                  <li>Compliance support</li>
                  <li>Integration with ADP, Gusto, or other providers</li>
                </ul>
                <p className="pricing-note">
                  * Free when bundled with accounting services
                </p>
                <button className="signup-button" onClick={() => window.location.href = '/books'}>
                  Sign Up Now
                </button>
              </div>

              <div className="pricing-card growth">
                <h3>Growth</h3>
                <div className="price">$100<span>/month</span></div>
                <p className="pricing-description">For 6-20 employees</p>
                <ul className="pricing-features">
                  <li>Everything in Starter, plus:</li>
                  <li>Advanced reporting</li>
                  <li>Priority support</li>
                  <li>Custom workflows</li>
                  <li>Dedicated account manager</li>
                </ul>
                <p className="pricing-note">
                  * Third-party payroll provider fees are separate
                </p>
                <button className="get-started-button" onClick={() => handleOpenModal('growth')}>
                  Get Started
                </button>
              </div>

              <div className="pricing-card scale">
                <h3>Scale</h3>
                <div className="price">Custom</div>
                <p className="pricing-description">For 20+ employees</p>
                <ul className="pricing-features">
                  <li>Everything in Growth, plus:</li>
                  <li>Enterprise-grade security</li>
                  <li>Custom integrations</li>
                  <li>Advanced analytics</li>
                  <li>24/7 priority support</li>
                </ul>
                <button className="book-call-button" onClick={() => window.location.href = '/contact'}>
                  Book a Call
                </button>
              </div>
            </div>
          </section>

          <section className="recruiting-services">
            <h2>Global Recruitment Services</h2>
            <div className="recruiting-intro">
              <p>
                We provide comprehensive recruitment solutions for both contract and full-time positions, 
                helping you build your team efficiently across the United States and India. Our global 
                reach allows you to access top talent while optimizing costs.
              </p>
            </div>

            <div className="recruiting-grid">
              <div className="recruiting-card">
                <div className="recruiting-icon">🇺🇸</div>
                <h3>US Recruitment</h3>
                <div className="recruiting-content">
                  <h4>Full-Time Positions</h4>
                  <ul>
                    <li>Executive & Leadership roles</li>
                    <li>Technical & Engineering positions</li>
                    <li>Finance & Accounting professionals</li>
                    <li>Sales & Marketing experts</li>
                  </ul>

                  <h4>Contract Positions</h4>
                  <ul>
                    <li>Project-based hiring</li>
                    <li>Contract-to-hire options</li>
                    <li>Short-term staffing solutions</li>
                    <li>Specialized consultants</li>
                  </ul>
                </div>
              </div>

              <div className="recruiting-card">
                <div className="recruiting-icon">🇮🇳</div>
                <h3>India Recruitment</h3>
                <div className="recruiting-content">
                  <h4>Cost-Effective Solutions</h4>
                  <ul>
                    <li>Offshore development teams</li>
                    <li>Remote work arrangements</li>
                    <li>Dedicated offshore centers</li>
                    <li>Cross-border collaboration</li>
                  </ul>

                  <h4>Contract-to-Company (C2C)</h4>
                  <ul>
                    <li>Managed service providers</li>
                    <li>Technology consulting firms</li>
                    <li>Professional service companies</li>
                    <li>Custom staffing solutions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="recruiting-benefits">
              <h3>Why Choose Our Recruitment Services?</h3>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <div className="benefit-icon">🎯</div>
                  <h4>Targeted Hiring</h4>
                  <p>Precise candidate matching based on your specific requirements and company culture</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🌍</div>
                  <h4>Global Reach</h4>
                  <p>Access to talent pools in both US and India markets for optimal hiring decisions</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <h4>Quick Turnaround</h4>
                  <p>Efficient hiring process with rapid candidate screening and placement</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">💰</div>
                  <h4>Cost Optimization</h4>
                  <p>Flexible hiring models to balance quality and cost-effectiveness</p>
                </div>
              </div>
            </div>

            <div className="recruiting-cta">
              <p>Ready to build your dream team?</p>
              <button 
                className="contact-button" 
                onClick={() => handleOpenModal('recruitment')}
              >
                Contact Our Recruitment Team
              </button>
            </div>
          </section>
          
          <section className="tools-flexibility">
            <h2>Flexible HR Solutions for Your Business</h2>
            <p>Our adaptive approach to HR services means you can choose what works best for your team's needs.</p>
            
            <div className="tools-options">
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>🔄</span>
                </div>
                <h3>Use Our HR Platform</h3>
                <p>Access our comprehensive HR platform that streamlines payroll, benefits, and recruitment processes with AI-powered automation and expert support.</p>
              </div>
              
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>⚙️</span>
                </div>
                <h3>Use Your Existing Tools</h3>
                <p>Already have HR software? We can integrate with your existing systems, including ADP, Gusto, or any other platform you prefer.</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {showModal && (
        <SendMessageModal
          formName={modalType === 'recruitment' ? 'Recruitment Services' : 'Growth Payroll Package'}
          onClose={handleCloseModal}
          showDatePicker={true}
        />
      )}
    </div>
  );
}

export default People; 